import { memo, type ReactNode } from 'react'
import { clsx } from 'clsx'

import css from './Field.module.scss'

interface FieldProps {
  /** Id of the control this field labels. */
  controlId: string
  label?: ReactNode
  /** Content beside the label, outside its clickable control association. */
  labelAccessory?: ReactNode
  /** Helper text shown under the label. Replaces the per-page `.sectionHint` copies. */
  hint?: ReactNode
  error?: string
  required?: boolean
  hintId?: string
  errorId?: string
  /** `inline` puts the label after the control, for checkboxes and switches. */
  layout?: 'stacked' | 'inline'
  children: ReactNode
  className?: string
}

/**
 * Owns label, required marker, helper text, and error for every control.
 * Primitives render this internally, so page-level call sites stay unchanged.
 */
function Field({
  controlId,
  label,
  labelAccessory,
  hint,
  error,
  required = false,
  hintId,
  errorId,
  layout = 'stacked',
  children,
  className
}: FieldProps) {
  const labelElement = label && (
    <label htmlFor={controlId} className={css.label}>
      {label}
      {required && (
        <span className={css.required} aria-hidden="true">
          *
        </span>
      )}
    </label>
  )

  return (
    <div className={clsx('Field', css.root, layout === 'inline' && css.inline, className)}>
      {labelElement &&
        (labelAccessory ? (
          <div className={css.labelRow}>
            {labelElement}
            {labelAccessory}
          </div>
        ) : (
          labelElement
        ))}

      {hint && (
        <p id={hintId} className={css.hint}>
          {hint}
        </p>
      )}

      <div className={css.control}>{children}</div>

      {error && (
        <span id={errorId} className={css.error} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

export default memo(Field)
